import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, UserCheck, FileText, DollarSign, Clock, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PartnerCooperativesPanel from "@/components/PartnerCooperativesPanel";

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownPartnerId, setOwnPartnerId] = useState(null);

  useEffect(() => {
    fetchStats();
    // Load own partner for non-admin users to render the linked cooperatives panel below the cards
    if (user?.id && user?.role !== "admin" && user?.role !== "operador") {
      axiosInstance
        .get(`/partners/by-user/${user.id}`)
        .then((res) => setOwnPartnerId(res.data?.id || null))
        .catch(() => {
          // User doesn't have a linked partner yet — panel simply won't render
          setOwnPartnerId(null);
        });
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  // Different stats for admin vs partner vs operador
  const statCards = (user?.role === 'admin' || user?.role === 'operador') ? [
    {
      title: "Parceiros Comerciais",
      value: stats?.partners || 0,
      icon: Users,
      color: "from-emerald-500 to-teal-600",
      testId: "stat-partners"
    },
    {
      title: "Cooperados",
      value: stats?.cooperatives || 0,
      icon: UserCheck,
      color: "from-blue-500 to-cyan-600",
      testId: "stat-cooperatives"
    },
    {
      title: "Faturas Totais",
      value: stats?.invoices || 0,
      icon: FileText,
      color: "from-purple-500 to-pink-600",
      testId: "stat-invoices"
    },
    ...(user?.role === 'admin' ? [{
      title: "Total Economizado pelos Cooperados",
      value: `R$ ${(stats?.total_revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: "from-green-500 to-emerald-600",
      testId: "stat-revenue"
    }] : []),
    {
      title: "Faturas Pendentes",
      value: stats?.pending_invoices || 0,
      icon: Clock,
      color: "from-orange-500 to-red-600",
      testId: "stat-pending"
    },
    {
      title: "Total kWh Contratado",
      value: `${(stats?.total_kwh_cooperatives || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh`,
      icon: Users,
      color: "from-indigo-500 to-purple-600",
      testId: "stat-total-kwh"
    }
  ] : [
    {
      title: "Cooperados",
      value: stats?.cooperatives_count || 0,
      icon: UserCheck,
      color: "from-blue-500 to-cyan-600",
      testId: "stat-cooperatives"
    },
    {
      title: "Faturas Emitidas",
      value: stats?.invoices_count || 0,
      icon: FileText,
      color: "from-purple-500 to-pink-600",
      testId: "stat-invoices"
    },
    {
      title: "Economia dos Cooperados",
      value: `R$ ${(stats?.total_economy || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: "from-green-500 to-emerald-600",
      testId: "stat-economy"
    },
    {
      title: "kWh Contratado",
      value: `${(stats?.total_kwh || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh`,
      icon: Users,
      color: "from-indigo-500 to-purple-600",
      testId: "stat-kwh"
    },
    {
      title: "Comissão Recebida",
      value: `R$ ${(stats?.commission_received || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: "from-emerald-500 to-teal-600",
      testId: "stat-commission-received"
    },
    {
      title: "Comissão a Receber",
      value: `R$ ${(stats?.commission_to_receive || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: Clock,
      color: "from-orange-500 to-red-600",
      testId: "stat-commission-pending"
    },
    {
      title: "Bônus Recebido",
      value: `R$ ${(stats?.bonus_received || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: "from-green-500 to-emerald-600",
      testId: "stat-bonus-received"
    },
    {
      title: "Bônus a Receber",
      value: `R$ ${(stats?.bonus_to_receive || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: Clock,
      color: "from-yellow-500 to-orange-600",
      testId: "stat-bonus-pending"
    }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
          <p className="text-gray-600">Visão geral do sistema Enercooper</p>
        </div>

        {user?.is_master && stats?.measurements_awaiting_send != null && (
          <Card
            className={`border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow ${stats.measurements_awaiting_send > 0 ? "bg-amber-50 border-l-4 border-l-amber-500" : "bg-emerald-50 border-l-4 border-l-emerald-500"}`}
            onClick={() => navigate("/powerplants-billing")}
            data-testid="master-awaiting-send-card"
          >
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats.measurements_awaiting_send > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Lembrete de Pendências — Medições Aguardando Envio</p>
                  <p className="text-xs text-gray-600">
                    {stats.measurements_awaiting_send > 0
                      ? "Há relatórios de medição prontos que ainda não foram enviados aos usineiros. Clique para abrir Relatório de Usinas."
                      : "Nenhuma medição aguardando envio. Tudo em dia!"}
                  </p>
                </div>
              </div>
              <span className={`text-3xl font-bold ${stats.measurements_awaiting_send > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="master-awaiting-send-count">
                {stats.measurements_awaiting_send}
              </span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={stat.testId}>
                  <div className={`h-2 bg-gradient-to-r ${stat.color}`}></div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <div className={`w-8 h-8 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      {stat.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-800">{stat.value}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* kWh by Distributor section for admin - com breakdown Autoconsumo vs Geração Compartilhada */}
        {user?.role === 'admin' && stats?.kwh_by_distributor && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">kWh Contratado por Distribuidora</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Separado por modalidade de compensação (Autoconsumo Remoto + Autoconsumo Remoto - Usina + Autoconsumo C · Geração Compartilhada)
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(stats.kwh_by_distributor)
                  .map(([distributor, data]) => {
                    // Backward compatibility: se vier número puro, converte para estrutura
                    const bucket = typeof data === 'number'
                      ? { autoconsumo: 0, geracao_compartilhada: data, total: data }
                      : data;
                    return { distributor, ...bucket };
                  })
                  .sort((a, b) => (b.total || 0) - (a.total || 0))
                  .map((row) => (
                    <div
                      key={row.distributor}
                      className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200"
                      data-testid={`kwh-card-${row.distributor}`}
                    >
                      <p className="text-sm font-semibold text-gray-700 mb-2">{row.distributor}</p>
                      <p className="text-2xl font-bold text-blue-700 mb-3">
                        {(row.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span className="text-gray-600">Autoconsumo</span>
                          </span>
                          <span className="font-semibold text-emerald-700">
                            {(row.autoconsumo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                            <span className="text-gray-600">Geração Compart.</span>
                          </span>
                          <span className="font-semibold text-indigo-700">
                            {(row.geracao_compartilhada || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking de Parceiros por kWh - Admin Only (ABAIXO das distribuidoras) */}
        {user?.role === 'admin' && stats?.partners_ranking && stats.partners_ranking.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800">
                Ranking de Parceiros por kWh Contratado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">#</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Parceiro</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Cooperados</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Total kWh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.partners_ranking.map((partner, index) => (
                      <tr 
                        key={partner.partner_id} 
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          index === 0 ? 'bg-gradient-to-r from-yellow-50 to-amber-50' : 
                          index === 1 ? 'bg-gradient-to-r from-gray-50 to-slate-50' :
                          index === 2 ? 'bg-gradient-to-r from-orange-50 to-red-50' : ''
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                            index === 0 ? 'bg-yellow-500 text-white' : 
                            index === 1 ? 'bg-gray-400 text-white' :
                            index === 2 ? 'bg-orange-500 text-white' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {index + 1}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {index < 3 && (
                              <span className="text-2xl">
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </span>
                            )}
                            <span className={`font-medium ${index < 3 ? 'text-gray-800 text-lg' : 'text-gray-700'}`}>
                              {partner.partner_name}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                            {partner.cooperatives_count}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className={`font-bold ${index < 3 ? 'text-xl' : 'text-lg'} ${
                            index === 0 ? 'text-yellow-600' : 
                            index === 1 ? 'text-gray-600' :
                            index === 2 ? 'text-orange-600' :
                            'text-blue-700'
                          }`}>
                            {partner.total_kwh.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">kWh</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Bem-vindo, {user?.name}!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Você está logado como <span className="font-semibold text-emerald-600">{user?.role === 'admin' ? 'Administrador' : 'Usuário'}</span>.
              Use o menu lateral para navegar entre as diferentes seções do sistema.
            </p>
          </CardContent>
        </Card>

        {/* Painel de cooperados vinculados — visível apenas para parceiros (não-admin/não-operador) */}
        {ownPartnerId && user?.role !== 'admin' && user?.role !== 'operador' && (
          <PartnerCooperativesPanel
            partnerId={ownPartnerId}
            showFilters="date"
            showHierarchy={true}
            title={<>Meus cooperados vinculados</>}
          />
        )}
      </div>
    </Layout>
  );
}